import { FILE_TYPE_PNG, MAX_PNG_SIZE, MAX_SAFARI_CANVAS_SIZE } from '@constants/global';

const createImage = (url: string) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

function limitSize(width: number, height: number, maximumPixels: number) {
  const requiredPixels = width * height;
  if (requiredPixels <= maximumPixels) return { width, height };

  const scalar = Math.sqrt(maximumPixels / requiredPixels);

  return {
    width: Math.floor(width * scalar),
    height: Math.floor(height * scalar),
  };
}

export const isLargePng = async (type: string, fileEl: { src: string; file: File }) => {
  const isPng = type.includes(FILE_TYPE_PNG);
  if (!isPng) return false;
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.src = fileEl.src;

    img.onload = () => {
      const fileWidth = img.width;
      const isBiggerSize = fileWidth > MAX_PNG_SIZE;
      resolve(isBiggerSize);
    };

    img.onerror = () => {
      // Handle image loading error here if needed
      resolve(reject);
    };
  });
};

export const getImagesList = (files: FileList): { src: string; file: File }[] => {
  const imagesList = [];
  for (let i = 0; i < files.length; i++) {
    imagesList.push({ file: files[i], src: URL.createObjectURL(files[i]) });
  }
  return imagesList;
};

export async function getCroppedImg(
  imageSrc: string,
  imgType: string,
  pixelCrop: {
    width: number;
    height: number;
    x: number;
    y: number;
  },
  rotation: number = 0
) {
  const image = (await createImage(imageSrc)) as HTMLImageElement;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return;
  }

  // Create a temporary canvas for the rotated image
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) {
    return;
  }

  // Set the temp canvas size to fit the rotated image
  const rotatedSize = {
    width:
      Math.abs(image.width * Math.cos(getRadianAngle(rotation))) +
      Math.abs(image.height * Math.sin(getRadianAngle(rotation))),
    height:
      Math.abs(image.width * Math.sin(getRadianAngle(rotation))) +
      Math.abs(image.height * Math.cos(getRadianAngle(rotation))),
  };

  // Limit the size while maintaining aspect ratio
  const limitedSize = limitSize(rotatedSize.width, rotatedSize.height, MAX_SAFARI_CANVAS_SIZE);
  const scale = Math.min(limitedSize.width / rotatedSize.width, limitedSize.height / rotatedSize.height);

  tempCanvas.width = limitedSize.width;
  tempCanvas.height = limitedSize.height;

  // Center and rotate the image
  tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
  tempCtx.rotate(getRadianAngle(rotation));
  tempCtx.translate(-tempCanvas.width / 2, -tempCanvas.height / 2);

  // Calculate scaled dimensions
  const scaledWidth = image.width * scale;
  const scaledHeight = image.height * scale;
  const drawX = (tempCanvas.width - scaledWidth) / 2;
  const drawY = (tempCanvas.height - scaledHeight) / 2;

  // Draw the image on the temp canvas
  tempCtx.drawImage(image, drawX, drawY, scaledWidth, scaledHeight);

  // Set up the final canvas with the crop dimensions
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Calculate the crop position relative to the scaled and rotated image
  const cropX = (tempCanvas.width - scaledWidth) / 2 + pixelCrop.x * scale;
  const cropY = (tempCanvas.height - scaledHeight) / 2 + pixelCrop.y * scale;

  // Draw the cropped portion
  ctx.drawImage(
    tempCanvas,
    cropX,
    cropY,
    pixelCrop.width * scale,
    pixelCrop.height * scale,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      // eslint-disable-next-line
      // @ts-ignore
      const file = new File([blob], 'cropped-image', {
        lastModified: new Date(),
        type: imgType,
      });
      if (blob) {
        resolve({ src: URL.createObjectURL(blob), file });
      }
    }, imgType);
  });
}
